import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  emptyUiProgress,
  isTipDone,
  isTourComplete,
  mergeUiProgress,
  normalizeUiProgress,
} from './progress';
import type { TipTokenPrefix, UiProgress } from './types';

type LoadFn = () => Promise<{ ui_progress: UiProgress }>;
type PatchFn = (
  patch: Partial<UiProgress> | Record<string, unknown>
) => Promise<{ ui_progress: UiProgress }>;

type TipProgressContextValue = {
  tokenPrefix: TipTokenPrefix;
  progress: UiProgress;
  ready: boolean;
  patchProgress: (patch: Record<string, unknown>) => void;
  markTourStep: (tourId: string, stepId: string, version: number) => void;
  completeTour: (tourId: string, version: number) => void;
  skipTour: (tourId: string, version: number) => void;
  resetTour: (tourId: string) => void;
  dismissTip: (tipId: string, version: number) => void;
  isTourDone: (tourId: string, version: number) => boolean;
  isTipDismissed: (tipId: string, version: number) => boolean;
};

const TipProgressContext = createContext<TipProgressContextValue | null>(null);

const DEBOUNCE_MS = 400;

type ProviderProps = {
  tokenPrefix: TipTokenPrefix;
  load: LoadFn;
  patch: PatchFn;
  /** When false, skip network (e.g. unauthenticated). */
  enabled?: boolean;
  children: ReactNode;
};

export const TipProgressProvider = ({
  tokenPrefix,
  load,
  patch,
  enabled = true,
  children,
}: ProviderProps) => {
  const [progress, setProgress] = useState<UiProgress>(emptyUiProgress);
  const [ready, setReady] = useState(false);
  const pendingRef = useRef<Record<string, unknown> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const patchRef = useRef(patch);
  patchRef.current = patch;

  useEffect(() => {
    if (!enabled) {
      setProgress(emptyUiProgress());
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    setReady(false);
    load()
      .then((data) => {
        if (cancelled) return;
        setProgress(normalizeUiProgress(data?.ui_progress));
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProgress(emptyUiProgress());
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, load]);

  const flush = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || !enabled) return;
    try {
      const data = await patchRef.current(pending);
      setProgress((prev) => {
        let next = normalizeUiProgress(data?.ui_progress);
        // Patches that landed while the request was in flight stay local.
        if (pendingRef.current) {
          next = mergeUiProgress(next, pendingRef.current);
        }
        // Never let a stale in-flight response wipe Done/Skip that landed locally.
        for (const [id, entry] of Object.entries(prev.walkthroughs)) {
          const remote = next.walkthroughs[id];
          const localDone = Boolean(entry.completed_at || entry.skipped_at);
          const remoteDone = Boolean(
            remote?.completed_at || remote?.skipped_at
          );
          if (localDone && !remoteDone) {
            next = {
              ...next,
              walkthroughs: { ...next.walkthroughs, [id]: entry },
            };
          }
        }
        return next;
      });
    } catch {
      // Keep optimistic local state; next successful patch reconciles.
    }
  }, [enabled]);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flush();
    }, DEBOUNCE_MS);
  }, [flush]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const patchProgress = useCallback(
    (partial: Record<string, unknown>) => {
      setProgress((prev) => mergeUiProgress(prev, partial));
      pendingRef.current = mergeUiProgress(pendingRef.current ?? {}, partial);
      scheduleFlush();
    },
    [scheduleFlush]
  );

  const markTourStep = useCallback(
    (tourId: string, stepId: string, version: number) => {
      patchProgress({
        walkthroughs: {
          [tourId]: { step: stepId, version },
        },
      });
    },
    [patchProgress]
  );

  const completeTour = useCallback(
    (tourId: string, version: number) => {
      patchProgress({
        walkthroughs: {
          [tourId]: {
            completed_at: new Date().toISOString(),
            version,
          },
        },
      });
    },
    [patchProgress]
  );

  const skipTour = useCallback(
    (tourId: string, version: number) => {
      patchProgress({
        walkthroughs: {
          [tourId]: {
            skipped_at: new Date().toISOString(),
            version,
          },
        },
      });
    },
    [patchProgress]
  );

  const resetTour = useCallback(
    (tourId: string) => {
      patchProgress({
        walkthroughs: {
          [tourId]: null,
        },
      });
    },
    [patchProgress]
  );

  const dismissTip = useCallback(
    (tipId: string, version: number) => {
      patchProgress({
        tips: {
          [tipId]: {
            dismissed_at: new Date().toISOString(),
            version,
          },
        },
      });
    },
    [patchProgress]
  );

  const value = useMemo<TipProgressContextValue>(
    () => ({
      tokenPrefix,
      progress,
      ready,
      patchProgress,
      markTourStep,
      completeTour,
      skipTour,
      resetTour,
      dismissTip,
      isTourDone: (tourId, version) =>
        isTourComplete(progress, tourId, version),
      isTipDismissed: (tipId, version) => isTipDone(progress, tipId, version),
    }),
    [
      tokenPrefix,
      progress,
      ready,
      patchProgress,
      markTourStep,
      completeTour,
      skipTour,
      resetTour,
      dismissTip,
    ]
  );

  return (
    <TipProgressContext.Provider value={value}>
      {children}
    </TipProgressContext.Provider>
  );
};

export const useTipProgress = (): TipProgressContextValue => {
  const ctx = useContext(TipProgressContext);
  if (!ctx) {
    throw new Error('useTipProgress must be used within TipProgressProvider');
  }
  return ctx;
};

export const useOptionalTipProgress = (): TipProgressContextValue | null =>
  useContext(TipProgressContext);
